import { useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Input, NativeSelect } from '@immo/ui-kit';

/*
 * List-page filter toolbar — batch-7 spec, 44px tall, hairline bottom border.
 * Layout: preset filter pills · spacer · free-text search.
 */

// Matches the customer app's CompanyCombobox. The debounce lives here rather
// than behind a prop, because a consumer cannot see what its own onSearchChange
// costs — on three of these pages one keystroke is a router navigation and an
// HTTP request, and the next such page would forget to pass the prop.
// One search is one history entry: the first commit pushes, and refinements
// made while the field stays focused report `continuing` so the page replaces.
export const SEARCH_DEBOUNCE_MS = 300;

// `h-7` on `FilterPill`; `gap-2` on the pill group. Stated here once so the
// cap below is derived rather than a restated literal (BL-034).
const PILL_HEIGHT_PX = 28;
const PILL_GAP_PX = 8;
const PILL_ROW_LIMIT = 2;
// `focus-visible:shadow-focus` paints outside the pill's border box. Without
// this the cap's `overflow-hidden` clips that ring along the bottom edge of
// the last row, so keyboard focus there reads as a partial ring.
const FOCUS_RING_PADDING_PX = 3;

/** Two pill rows plus room for the last row's focus ring — the height the group reserves when an `overflow` outlet is supplied, whether or not its content reaches it. */
export const PILL_GROUP_HEIGHT_PX =
  PILL_ROW_LIMIT * PILL_HEIGHT_PX + (PILL_ROW_LIMIT - 1) * PILL_GAP_PX + FOCUS_RING_PADDING_PX;

export type FilterBarOverflow = {
  /** Accessible name of the control. DE: „Weitere Kunden". */
  readonly label: string;
  /** Shown while nothing is chosen. DE: „Weitere Kunden …". */
  readonly placeholder: string;
  /** The tail: everything not currently rendered as a pill. */
  readonly items: ReadonlyArray<{ readonly id: string; readonly label: string }>;
  readonly onSelect: (id: string) => void;
};

export type SearchCommit = {
  /** `true` when this commit refines a search already pushed from this field since it was focused. */
  readonly continuing: boolean;
};

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

type FilterBarProps = {
  /**
   * The DATA-DRIVEN pills — the ones `overflow` can still reach. Capped at two
   * rows and clipped when `overflow` is supplied.
   */
  readonly pills?: ReactNode;
  /**
   * Pills the `overflow` outlet CANNOT reach — a fixed set of status filters, a
   * toggle. They render before `pills` and OUTSIDE the clip box, because a cap
   * over a pill with no outlet does not defer that filter, it deletes it
   * (BL-073, BL-122). Rendering them first is not enough on its own: at a
   * narrow width the two rows fill up before the fixed set ends, and the
   * remainder vanishes with nothing to reach it by (IMM-969).
   */
  readonly fixedPills?: ReactNode;
  /**
   * The free-text search. Omit `onSearchChange` on a surface that has no
   * search — the Gelöschte Deals list — and the toolbar carries pills only, rather
   * than an input that does nothing (BL-057).
   */
  readonly searchValue?: string;
  readonly searchPlaceholder?: string;
  readonly onSearchChange?: (next: string, commit: SearchCommit) => void;
  /**
   * The tail of a DATA-DRIVEN pill list. Supplying it reserves two rows'
   * height for the pill group and clips anything past it; omitting it leaves
   * the group unbounded, exactly as today. `items: []` is valid and means
   * "reserve the height, render no outlet" — the outlet itself only renders
   * when `overflow.items.length > 0`. A cap with no outlet hides filters
   * (BL-073, BL-122).
   */
  readonly overflow?: FilterBarOverflow;
  /** The last control of the row („Spalten"). Shares the select's and the search's flex child, so they wrap together (IMM-980). */
  readonly trailing?: ReactNode;
};

export function FilterBar({
  pills,
  fixedPills,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  overflow,
  trailing,
}: FilterBarProps) {
  const showOutlet = overflow !== undefined && overflow.items.length > 0;
  // Only Bewertungen passes all three. Narrowing keeps „Spalten" on the group's line at 1280px (IMM-1074.6).
  const compact = showOutlet && onSearchChange !== undefined && trailing !== undefined;

  return (
    <nav
      role="toolbar"
      aria-label="Filters"
      className="flex min-h-toolbar shrink-0 flex-wrap items-center gap-2 border-b border-border bg-surface px-4 py-1.5"
    >
      {/*
       * The pills WRAP; the labels do not (IMM-643). Once a label may not wrap,
       * the width pressure has to land somewhere — and a second row of pills is
       * the only place that keeps every filter both readable and reachable. The
       * alternative, scrolling this row, hides filters behind a scrollbar that
       * is itself awkward to reach in these apps (IMM-617, IMM-636 / BL-051).
       *
       * The group carries the `flex-1` that used to belong to a spacer div, so
       * the search stays pinned right and a pill-less toolbar is unchanged.
       * `min-w-0` lets the group actually shrink instead of pushing the search
       * out of the row.
       */}
      {/*
       * `overflow-clip`, not `overflow-hidden`: a `hidden` box is still a scroll
       * container, so Tab onto a clipped pill scrolls the group to reveal it and
       * leaves it scrolled — the status pills, „Testumgebungen" and the active
       * workspace pill then sit out of view (IMM-929 QA-9). `clip` cannot scroll.
       *
       * `self-start` on both groups, so their first rows share a top edge: the
       * nav centres its items, and a one-row fixed set beside a two-row capped
       * group would otherwise float half a row below it.
       */}
      {fixedPills !== undefined && (
        <div className="flex min-w-0 flex-wrap items-center gap-2 self-start" data-testid="filter-bar-fixed-pills">
          {fixedPills}
        </div>
      )}
      <div
        data-testid="filter-bar-pill-group"
        className={`flex min-w-0 flex-1 flex-wrap items-center gap-2${overflow !== undefined ? ' self-start basis-[28rem] overflow-clip pb-[3px]' : ''}`}
        style={overflow !== undefined ? { height: PILL_GROUP_HEIGHT_PX } : undefined}
      >
        {pills}
      </div>
      {/*
       * The select and the search are ONE flex child, not two-plus (IMM-980).
       * `FilterSearch` returns a fragment, so left ungrouped they wrap
       * independently under the `<nav>`'s new `flex-wrap`: the group would
       * still clip, and the search icon would strand on the line above its
       * own input. Rendered only when it would hold something — an
       * always-rendered empty wrapper still costs the nav's `gap-2` on the
       * pills-only toolbars that pass neither prop. `trailing` alone is enough
       * to render it.
       *
       * The `basis-[28rem]` (448px) floor on the group above is what makes
       * this wrap on CONTENT, not on a breakpoint: 448 (group) + 8 (gap) +
       * 480 (this child at 192 select + 8 + 16 icon + 8 + 256 input) = 936px
       * of content width, i.e. viewport < 1257px with the sidebar expanded
       * (`--space-sidebar` 288 + 1 border + 32 `px-4`). Above that the group
       * settles at 471px at 1280px, which clears the 448px floor — a floor
       * >= 471px would wrap there too and re-break IMM-929's 80px toolbar.
       *
       * With the outlet, a search and `trailing`, the child is 160 + 8 + 16 + 8
       * + 160 + 8 + ~100 („Spalten") = ~460px, which leaves ~491px for the group
       * at 1280px. Without narrowing it would be ~588px, leaving ~363px — under
       * the 448px floor, so the controls would wrap onto a line of their own.
       * Only the Bewertungen toolbar passes all three (IMM-1074.6).
       */}
      {(showOutlet || onSearchChange !== undefined || trailing !== undefined) && (
        <div data-testid="filter-bar-controls" className="flex shrink-0 items-center gap-2">
          {showOutlet && (
            <div className={compact ? 'w-40 shrink-0' : 'w-48 shrink-0'}>
              <NativeSelect
                size="sm"
                value=""
                placeholder={overflow.placeholder}
                aria-label={overflow.label}
                options={overflow.items.map((item) => ({ value: item.id, label: item.label }))}
                onChange={overflow.onSelect}
              />
            </div>
          )}
          {onSearchChange !== undefined && (
            <FilterSearch
              value={searchValue ?? ''}
              placeholder={searchPlaceholder ?? ''}
              onChange={onSearchChange}
              compact={compact}
            />
          )}
          {trailing}
        </div>
      )}
    </nav>
  );
}

type FilterSearchProps = {
  readonly value: string;
  readonly placeholder: string;
  readonly onChange: (next: string, commit: SearchCommit) => void;
  readonly compact: boolean;
};

function FilterSearch({
  value: searchValue,
  placeholder: searchPlaceholder,
  onChange: onSearchChange,
  compact,
}: FilterSearchProps) {
  const [draft, setDraft] = useState(searchValue);
  const lastCommitted = useRef(searchValue);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionOpen = useRef(false);

  // Follow the prop when it changes from outside (back/forward, a cleared
  // filter, a fresh URL), but ignore the prop update our own commit causes —
  // resyncing on that reverts characters typed while the commit re-rendered.
  // An outside change also DROPS a pending commit: letting it fire would undo
  // the navigation the operator just made with a term they abandoned.
  useEffect(() => {
    if (searchValue === lastCommitted.current) return;

    cancelPending();
    sessionOpen.current = false;
    lastCommitted.current = searchValue;
    setDraft(searchValue);
  }, [searchValue]);

  useEffect(
    () => () => {
      if (pending.current !== null) clearTimeout(pending.current);
    },
    [],
  );

  function cancelPending(): void {
    if (pending.current === null) return;

    clearTimeout(pending.current);
    pending.current = null;
  }

  function commit(next: string): void {
    cancelPending();

    if (next === lastCommitted.current) return;

    lastCommitted.current = next;
    onSearchChange(next, { continuing: sessionOpen.current });
    sessionOpen.current = true;
  }

  function handleChange(next: string): void {
    setDraft(next);
    cancelPending();
    pending.current = setTimeout(() => {
      pending.current = null;
      commit(next);
    }, SEARCH_DEBOUNCE_MS);
  }

  return (
    <>
      <SearchIcon data-testid="filter-search-icon" className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
      <Input
        type="search"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(draft);
        }}
        onBlur={() => {
          commit(draft);
          sessionOpen.current = false;
        }}
        placeholder={searchPlaceholder}
        className={compact ? 'h-8 w-40 shrink-0' : 'h-8 w-64 shrink-0'}
      />
    </>
  );
}

type FilterPillProps = {
  readonly icon?: IconComponent;
  readonly label: string;
  readonly active: boolean;
  readonly onToggle: () => void;
  /**
   * How many rows this pill would return, shown beside its label.
   *
   * Optional, and absent is NOT zero: a caller that cannot count says nothing
   * rather than claiming an empty result. `tone="attention"` marks a count an
   * operator is meant to act on — a triage backlog rather than a total.
   */
  readonly count?: number;
  readonly countTone?: 'neutral' | 'attention';
};

export function FilterPill({ icon: Icon, label, active, onToggle, count, countTone = 'neutral' }: FilterPillProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={`inline-flex h-7 items-center gap-1.5 rounded-sm border px-2 text-base-sm transition-colors duration-micro ease-out focus-visible:outline-none focus-visible:shadow-focus ${
        active
          ? 'border-border-strong bg-surface font-medium text-text-primary'
          : 'border-border bg-surface text-text-secondary hover:bg-hover-bg'
      }`}
    >
      {Icon !== undefined && (
        <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? 'text-text-primary' : 'text-text-tertiary'}`} aria-hidden />
      )}
      {/*
       * `truncate` is `whitespace-nowrap` + `overflow-hidden` + ellipsis, and
       * all three are load-bearing here (IMM-643 / BL-056): the button is `h-7`,
       * so a label that wraps paints its second line OUTSIDE the border. A
       * fixed-size control must forbid its label from wrapping.
       *
       * The `max-w` bounds the one input this component cannot bound: the
       * Bewertungen toolbar renders WORKSPACE NAMES, customer-entered text with
       * no length limit, which `whitespace-nowrap` alone would turn into an
       * arbitrarily wide pill. It sits on the label rather than on the button so
       * the label keeps its own intrinsic minimum — a `min-w-0` label inside a
       * bounded button has no floor and clips mid-glyph instead of ellipsising
       * (BL-043). 14rem is far above the width an ellipsis needs, and far above
       * any label that fits today, so short pills are unchanged.
       */}
      <span className="max-w-[14rem] truncate" title={label}>
        {label}
      </span>
      {/* `tabular-nums` so a row of pills does not reflow as the counts change
          width — and outside the truncating label, because a count that
          ellipsised would be worse than no count at all. */}
      {/* A real space, not the flex `gap`: the gap is visual only, so without it
          the accessible name concatenates to „Alle22" and a screen reader reads
          the label and its count as one word. */}
      {count !== undefined && ' '}
      {count !== undefined && (
        <span
          className={`shrink-0 text-xs tabular-nums ${
            countTone === 'attention' && count > 0 ? 'font-medium text-info-700' : 'text-text-tertiary'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

type FilterPillGroupProps<T extends string> = {
  /** Accessible name of the group. DE: „Status". */
  readonly label: string;
  /** DE: „Alle". */
  readonly allLabel: string;
  readonly options: ReadonlyArray<{ readonly value: T; readonly label: string }>;
  /** `null` = „Alle". */
  readonly value: T | null;
  readonly onChange: (next: T | null) => void;
  readonly icon?: IconComponent;
  /**
   * Render `label` visibly before the pills. For a toolbar with two groups, whose "all" pills (`allLabel`)
   * are otherwise told apart by icon alone (IMM-1100.2).
   */
  readonly showLabel?: boolean;
};

/**
 * A single-select group with „Alle": exactly one pill is pressed, and clicking the
 * pressed one does nothing. Its options are fixed, so it goes in `fixedPills` —
 * no `overflow` outlet could reach a pill the cap clipped.
 */
export function FilterPillGroup<T extends string>({
  label,
  allLabel,
  options,
  value,
  onChange,
  icon,
  showLabel = false,
}: FilterPillGroupProps<T>) {
  const iconProps = icon !== undefined ? { icon } : {};
  const choose = (next: T | null) => {
    if (next !== value) onChange(next);
  };

  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2">
      {showLabel && (
        <span aria-hidden className="text-base-sm text-text-tertiary">
          {label}
        </span>
      )}
      <FilterPill {...iconProps} label={allLabel} active={value === null} onToggle={() => choose(null)} />
      {options.map((option) => (
        <FilterPill
          key={option.value}
          {...iconProps}
          label={option.label}
          active={value === option.value}
          onToggle={() => choose(option.value)}
        />
      ))}
    </div>
  );
}

function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  );
}
